import { Schema } from 'yup';
import { applyLiveContextTransform } from './applyLiveContextTransform.ts';
import {
  hasLiveContextTransform,
  hasLiveTransformApplied,
  markLiveTransformApplied,
} from './live.ts';
import { VALIDATION_VALUES } from './validationValuesKey.ts';

/**
 * Monkey-patch su `Schema.prototype.cast`/`validate`/`validateSync`/`resolve`
 * per applicare automaticamente le transform live registrate sullo schema
 * (`.setLiveContextTransform(...)`, `.setLiveValuesTransform(...)`).
 *
 * ORDINE DI CARICAMENTO (vedi `initYup.ts`): DOPO `lightweightResolve.ts`.
 * Quel file patcha `resolve` per gestire `_resolveDepth` (clone optimization).
 * Qui catturiamo quella versione come `previousResolve` e la wrappiamo:
 * il nostro `resolve` arricchisce le options e poi delega a `previousResolve`.
 *
 * Catena finale: yup originale ← lightweightResolve ← livePatch
 */

const originalCast = Schema.prototype.cast;
const originalValidate = Schema.prototype.validate;
const originalValidateSync = Schema.prototype.validateSync;
const previousResolve = Schema.prototype.resolve;

/**
 * Calcola il context arricchito. Tre rami:
 * 1. Context già marcato live → invariato (idempotenza nelle ricorsioni yup).
 * 2. Iniezione di `VALIDATION_VALUES`, SEMPRE valida per tutti gli schemi
 *    (anche form "normali" fuori dall'engine). I values vanno come default:
 *    una chiave già presente nel context del chiamante VINCE (es.
 *    `useGetRepeatableChildrenDefault` casta `{}` ma passa i valori del form).
 * 3. Schema con transform → `applyLiveContextTransform` la invoca e marca il risultato.
 */
function applyLiveContextIfNecessary(
  schema: Schema,
  values: any,
  options: any,
): any {
  // Early-return su context già live: evita di invocare `applyLiveContextTransform`
  // e di allocare un nuovo oggetto options nelle ricorsioni yup (hot path).
  if (hasLiveTransformApplied(options?.context)) return options.context;

  const nextContext = { [VALIDATION_VALUES]: values, ...options?.context };

  // LiveContextTransform personalizzata dello schema, se presente: riceve il
  // context già arricchito con i values e marca lei il risultato.
  if (hasLiveContextTransform(schema)) {
    return applyLiveContextTransform(schema, values, nextContext);
  }

  markLiveTransformApplied(nextContext);
  return nextContext;
}

// Early-return su context già live: evita l'allocazione di `{...options, context}`
// nelle ricorsioni yup sui campi nested (hot path).

Schema.prototype.cast = function (this: Schema, value: any, options?: any) {
  if (hasLiveTransformApplied(options?.context)) {
    return originalCast.call(this, value, options);
  }
  const context = applyLiveContextIfNecessary(this, value, options);
  return originalCast.call(this, value, { ...options, context });
};

Schema.prototype.validate = function (this: Schema, value: any, options?: any) {
  if (hasLiveTransformApplied(options?.context)) {
    return originalValidate.call(this, value, options);
  }
  const context = applyLiveContextIfNecessary(this, value, options);
  return originalValidate.call(this, value, { ...options, context });
};

Schema.prototype.validateSync = function (
  this: Schema,
  value: any,
  options?: any,
) {
  if (hasLiveTransformApplied(options?.context)) {
    return originalValidateSync.call(this, value, options);
  }
  const context = applyLiveContextIfNecessary(this, value, options);
  return originalValidateSync.call(this, value, { ...options, context });
};

/**
 * Resolve: arricchisce solo il context (i `values` non viaggiano per resolve).
 * Mirror dell'early-return di `lightweightResolve`: se non ci sono condizioni
 * non c'è nulla da risolvere — saltiamo direttamente.
 */
Schema.prototype.resolve = function (this: Schema, options: any) {
  if (!(this as any).conditions.length) return this;

  if (hasLiveTransformApplied(options?.context)) {
    return previousResolve.call(this, options);
  }

  return previousResolve.call(this, {
    ...options,
    context: applyLiveContextIfNecessary(this, options?.value, options),
  });
};
